import {
  loadEtrails,
  loadEtrailCategories,
  sendClientResponseToEtrail,
  markMessagesAsRead,
} from './services'

const getDefaultState = () => {
  return {
    etrails: [],
    postTravelFeedbackCategories: [],
    etrailCategories: [],
    loading: false,
    etrailsError: false,
    // The passenger id self-declared + OTP-verified specifically for
    // Current/All Communications access - deliberately separate from
    // auth/token (Profile/All Bookings/itinerary PDF), since a token
    // obtained there doesn't say *which* passenger it was. Cleared on
    // logout (resetStateAction below), not persisted, so a fresh
    // verification is required each new session too.
    verifiedPassengerId: null,
  }
}

const state = getDefaultState()

const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000

const isCompletedOverFortyEightHoursAgo = (etrail) => {
  if (!etrail.completedDate) return false
  const completedDate = new Date(etrail.completedDate).getTime()
  if (Number.isNaN(completedDate)) return false
  return Date.now() - completedDate > FORTY_EIGHT_HOURS_MS
}

const awaitsClientResponse = (etrail) => {
  return (etrail.messages || []).some(
    (message) => message.clientResponseAllowed === 'yes',
  )
}

const isUnreadMessage = (message) => !message.client && !message.read

const hasUnreadMessages = (etrail) => {
  return (etrail.messages || []).some(isUnreadMessage)
}

const blocksMovingToAllCommunications = (etrail) => {
  return awaitsClientResponse(etrail) || hasUnreadMessages(etrail)
}

const getters = {
  getEtrails: (state) => {
    return state.etrails
  },
  getCurrentEtrails: (state) => {
    return state.etrails.filter(
      (etrail) =>
        blocksMovingToAllCommunications(etrail) ||
        !(
          etrail.status === 'complete' &&
          isCompletedOverFortyEightHoursAgo(etrail)
        ),
    )
  },
  getCompletedEtrails: (state) => {
    return state.etrails.filter(
      (etrail) =>
        !blocksMovingToAllCommunications(etrail) &&
        etrail.status === 'complete' &&
        isCompletedOverFortyEightHoursAgo(etrail),
    )
  },
  getPostTravelFeedbackCategories: (state) => {
    return state.postTravelFeedbackCategories
  },
  getEtrailCategories: (state) => {
    return state.etrailCategories
  },
  getVerifiedPassengerId: (state) => {
    return state.verifiedPassengerId
  },
}
const actions = {
  async setEtrailsAction({ commit }, bookno) {
    const etrails = await loadEtrails(bookno)
    if (etrails.error) {
      commit('setEtrailsError', true)
      return
    }
    commit('setEtrails', etrails)
  },
  async setEtrailCategoriesAction({ commit }, bookno) {
    const etrailCategories = await loadEtrailCategories()
    if (
      etrailCategories.error ||
      !etrailCategories ||
      !etrailCategories.length
    ) {
      commit('setEtrailsError', true)
      return
    }

    commit('setEtrailCategories', etrailCategories)
  },
  async sendClientResponseToEtrailAction(
    { dispatch },
    { etrailId, payload, bookno },
  ) {
    const res = await sendClientResponseToEtrail(etrailId, payload)
    if (res && res.error) {
      return res
    }
    await dispatch('setEtrailsAction', bookno)
    return res
  },
  async markMessagesAsReadAction({ commit }, { etrailId, messageIds }) {
    if (!messageIds || !messageIds.length) return
    commit('setMessagesRead', { etrailId, messageIds })
    const res = await markMessagesAsRead(etrailId, messageIds)
    if (res && res.error) {
      return res
    }
    return res
  },
  setVerifiedPassengerIdAction({ commit }, passengerId) {
    commit('setVerifiedPassengerId', passengerId)
  },
  resetStateAction({ commit }) {
    commit('resetState')
  },
}

const mutations = {
  setEtrails(state, etrails) {
    state.etrails = etrails
  },
  setPostTravelFeedbackCategories(state, postTravelFeedbackCategories) {
    state.postTravelFeedbackCategories = postTravelFeedbackCategories
  },
  setEtrailCategories(state, etrailCategories) {
    state.etrailCategories = etrailCategories
  },
  setEtrailsError(state, etrailsError) {
    state.etrailsError = etrailsError
  },
  setMessagesRead(state, { etrailId, messageIds }) {
    state.etrails = state.etrails.map((etrail) => {
      if (etrail._id !== etrailId || !etrail.messages) return etrail
      return {
        ...etrail,
        messages: etrail.messages.map((message) =>
          messageIds.includes(message._id)
            ? { ...message, read: true }
            : message,
        ),
      }
    })
  },
  setVerifiedPassengerId(state, passengerId) {
    state.verifiedPassengerId = passengerId
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
}
